"use client";

import { useMemo } from "react";
import { ParaProvider as Provider } from "@getpara/react-sdk";
import { API_KEY, ENVIRONMENT } from "@/constants/para-const";
import {
  mainnet,
  polygon,
  bsc,
  base,
  arbitrum,
  optimism,
  avalanche,
  zkSync,
  linea,
  scroll,
  blast,
  zora,
  mantle,
  gnosis,
  fantom,
} from "wagmi/chains";
import { cookieStorage, createStorage, http } from "wagmi";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import "@getpara/react-sdk/styles.css";
import { getEthereumRpcUrl, getSolanaRpcUrl } from "@/lib/rpc";

const solanaNetwork = WalletAdapterNetwork.Mainnet;
const endpoint = getSolanaRpcUrl();
const walletConnectProjectId = process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID || "";
const evmChains = [
  mainnet,
  polygon,
  bsc,
  base,
  arbitrum,
  optimism,
  avalanche,
  zkSync,
  linea,
  scroll,
  blast,
  zora,
  mantle,
  gnosis,
  fantom,
] as const;
const evmTransports = {
  [mainnet.id]: http("/api/rpc?chain=ethereum"),
  [polygon.id]: http("/api/rpc?chain=polygon"),
  [bsc.id]: http("/api/rpc?chain=bsc"),
  [base.id]: http("/api/rpc?chain=base"),
  [arbitrum.id]: http("/api/rpc?chain=arbitrum"),
  [optimism.id]: http("/api/rpc?chain=optimism"),
  [avalanche.id]: http("/api/rpc?chain=avalanche"),
  [zkSync.id]: http("/api/rpc?chain=zksync"),
  [linea.id]: http("/api/rpc?chain=linea"),
  [scroll.id]: http("/api/rpc?chain=scroll"),
  [blast.id]: http("/api/rpc?chain=blast"),
  [zora.id]: http("/api/rpc?chain=zora"),
  [mantle.id]: http("/api/rpc?chain=mantle"),
  [gnosis.id]: http("/api/rpc?chain=gnosis"),
  [fantom.id]: http("/api/rpc?chain=fantom"),
};
const storage = createStorage({
  storage: cookieStorage,
});
const externalWallets: ("METAMASK" | "PHANTOM" | "SOLFLARE" | "COINBASE" | "WALLETCONNECT" | "RAINBOW")[] = [
  "METAMASK",
  "PHANTOM",
  "SOLFLARE",
  "COINBASE",
  "WALLETCONNECT",
  "RAINBOW",
];
const paraModalOAuthMethods: ("GOOGLE" | "TWITTER" | "DISCORD")[] = ["GOOGLE", "TWITTER", "DISCORD"];
const paraModalAuthLayout: ("AUTH:FULL" | "EXTERNAL:FULL")[] = ["AUTH:FULL", "EXTERNAL:FULL"];

export function ParaProvider({ children }: { children: React.ReactNode }) {
  const externalWalletConfig = useMemo(
    () => ({
      wallets: externalWallets,
      includeWalletVerification: false,
      evmConnector: {
        config: {
          chains: evmChains,
          transports: evmTransports,
          storage,
        },
      },
      solanaConnector: {
        config: {
          endpoint,
          chain: solanaNetwork,
          appIdentity: {
            uri:
              typeof window !== "undefined"
                ? `${window.location.protocol}//${window.location.host}`
                : "",
          },
        },
      },
      walletConnect: {
        projectId: walletConnectProjectId,
      },
    }),
    []
  );

  const paraModalConfig = useMemo(
    () => ({
      logo: "/logo.png",
      oAuthMethods: paraModalOAuthMethods,
      authLayout: paraModalAuthLayout,
      theme: {
        backgroundColor: "#000000",
        foregroundColor: "#FFFFFF",
        mode: "dark" as const,
      },
    }),
    []
  );

  return (
    <Provider
      paraClientConfig={{
        apiKey: API_KEY,
        env: ENVIRONMENT,
      }}
      externalWalletConfig={externalWalletConfig}
      config={{
        appName: "ForgeSwap",
        rpcUrl: getEthereumRpcUrl(),
      }}
      paraModalConfig={paraModalConfig}
    >
      {children}
    </Provider>
  );
}
